import { observable, action, reaction } from 'mobx'
import { fetchCategories, createCategory, updateCategory, deleteCategory, deleteProtocol, archiveProtocol, restoreProtocol, downloadProtocolFile, fetchUserList, fetchUserNames, patchMainManagerWithEmail, fetchArchiveList, fetchProtocols, fetchProtocolCountsByStatus, fetchProtocolForView, fetchProtocolForEdit, updateProtocol, updateProtocolStatus, createProtocol, uploadProtocolFiles, deleteProtocolFiles, fetchUsers, fetchUserPermissions, Category, UserOption, UserPermissions, MainManagerEntry, ArchiveItem, ProtocolItem, ProtocolViewData, ProtocolEditData, ProtocolCountsByStatus, ProtocolsListBody, CreateProtocolBody, UpdateProtocolBody, UpdateProtocolStatusBody, FileToUpload } from 'pages/Home/Protocols/api'
import { ModuleEnum } from 'utils/types'
import modulesStore from './modulesStore.store'
import snackBar from './snackbars.store'
import orgs from 'store/orgs.store'

class ProtocolsStore {
  @observable baseUrl = ''
  @observable authToken = ''
  @observable moduleId: number | null = null
  @observable communityId: number | null = null
  @observable categories: Category[] = []
  @observable loadingCategories = false
  @observable userList: UserOption[] = []
  @observable protocols: ProtocolItem[] = []
  @observable protocolsTotalCount = 0
  @observable loadingProtocols = false
  @observable protocolView: ProtocolViewData | null = null
  @observable loadingProtocolView = false
  @observable protocolEdit: ProtocolEditData | null = null
  @observable loadingProtocolEdit = false
  @observable protocolCounts: ProtocolCountsByStatus = { totalCount: 0, draftCount: 0, pendingApprovalCount: 0, approvedCount: 0, rejectedCount: 0 }
  @observable archiveItems: ArchiveItem[] = []
  @observable archiveTotalCount = 0
  @observable loadingArchive = false
  @observable users: UserOption[] = []
  @observable userPermissions: UserPermissions | null = null
  constructor() {
      reaction(
        () => orgs.currentOrgId,
        id => {
          if (id) {
            this.init(id).then(res => {
              if (res) {
                this.loadUserPermissions()
                this.loadUserList()
              }
            })
          }
        }
      )
    }

  @action init = async (communityId: number) => {
    const protocols = modulesStore.communityModules.filter(i => i.type === ModuleEnum.PROTOCOLS)
    if (protocols.length > 0) {
      const res = await modulesStore.openProtocolModule(protocols[0].module_id, communityId)
      this.moduleId = protocols[0].module_id
      this.communityId = communityId
      this.baseUrl = protocols[0].external_url
      this.authToken = res
      return true
    }

    return false
  }

  @action private refreshAuthToken = async () => {
    if (this.moduleId && this.communityId) {
      this.authToken = await modulesStore.openProtocolModule(this.moduleId, this.communityId)
    }
  }

  private withTokenRefresh = async <T>(fn: () => Promise<T>): Promise<T | null> => {
    const res = await fn()

    if ((res as any)?.unauthorized) {
      await this.refreshAuthToken()
      const retryRes = await fn()
      return (retryRes as any)?.unauthorized ? null : retryRes
    }

    return res
  }

  @action loadCategories = async (communityId: number) => {
    this.loadingCategories = true
    const data = await this.withTokenRefresh(() => fetchCategories(this.baseUrl, this.authToken, 'api/categories'))
    this.categories = (data as any) || []
    this.loadingCategories = false
  }

  @action createCategory = async (communityId: number, body: object) => {
    const res = await this.withTokenRefresh(() => createCategory(this.baseUrl, this.authToken, body))
    if (res) {
      snackBar.showSuccess('הקטגוריה נוצרה בהצלחה')
      await this.loadCategories(communityId)
    }
    return res
  }

  @action updateCategory = async (communityId: number, categoryId: number, body: object) => {
    const res = await this.withTokenRefresh(() => updateCategory(this.baseUrl, this.authToken, categoryId, body))
    if (res) {
      snackBar.showSuccess('הקטגוריה עודכנה בהצלחה')
      await this.loadCategories(communityId)
    }
    return res
  }

  @action deleteCategory = async (communityId: number, categoryId: number) => {
    const res = await this.withTokenRefresh(() => deleteCategory(this.baseUrl, this.authToken, categoryId))
    if (res) {
      snackBar.showSuccess('הקטגוריה נמחקה בהצלחה')
      await this.loadCategories(communityId)
    }
    return res
  }

  @action deleteProtocol = async (protocolId: number) => {
    const res = await this.withTokenRefresh(() => deleteProtocol(this.baseUrl, this.authToken, protocolId))
    if (res) {
      snackBar.showSuccess('הפרוטוקול נמחק בהצלחה')
    }
    return res
  }

  @action archiveProtocol = async (protocolId: number) => {
    const res = await this.withTokenRefresh(() => archiveProtocol(this.baseUrl, this.authToken, protocolId))
    if (res) {
      snackBar.showSuccess('הפרוטוקול הועבר לארכיון בהצלחה')
    }
    return res
  }

  @action restoreProtocol = async (protocolId: number) => {
    const res = await this.withTokenRefresh(() => restoreProtocol(this.baseUrl, this.authToken, protocolId))
    if (res) {
      snackBar.showSuccess('הפרוטוקול שוחזר בהצלחה')
    }
    return res
  }

  @action downloadProtocolFile = async (protocolId: number, fileId: number, fileName: string) => {
    return downloadProtocolFile(this.baseUrl, this.authToken, protocolId, fileId, fileName)
  }

  @action createProtocol = async (body: CreateProtocolBody) => {
    return this.withTokenRefresh(() => createProtocol(this.baseUrl, this.authToken, body))
  }

  @action uploadProtocolFiles = async (protocolId: number, files: FileToUpload[]) => {
    return this.withTokenRefresh(() => uploadProtocolFiles(this.baseUrl, this.authToken, protocolId, files))
  }

  @action deleteProtocolFiles = async (protocolId: number, fileIds: number[], filesToUpload: FileToUpload[] = []) => {
    return this.withTokenRefresh(() => deleteProtocolFiles(this.baseUrl, this.authToken, protocolId, fileIds, filesToUpload))
  }

  @action loadUserList = async () => {
    const data = await this.withTokenRefresh(() => fetchUserList(this.baseUrl, this.authToken))
    this.userList = (data as any) || []
  }

  @action loadUserNames = async (): Promise<UserOption[]> => {
    const data = await this.withTokenRefresh(() => fetchUserNames(this.baseUrl, this.authToken))
    return (data as any) || []
  }

  @action saveMainManagers = async (entries: MainManagerEntry[]): Promise<boolean> => {
    const res = await this.withTokenRefresh(() => patchMainManagerWithEmail(this.baseUrl, this.authToken, entries))
    return res !== null
  }

  @action loadProtocols = async (body: ProtocolsListBody) => {
    this.loadingProtocols = true
    const data = await this.withTokenRefresh(() => fetchProtocols(this.baseUrl, this.authToken, body))
    if (Array.isArray(data)) {
      this.protocols = data
      this.protocolsTotalCount = data.length
    } else {
      this.protocols = (data as any)?.protocols || []
      this.protocolsTotalCount = (data as any)?.totalCount || 0
    }
    this.loadingProtocols = false
  }

  @action loadProtocolForView = async (protocolId: number) => {
    this.loadingProtocolView = true
    this.protocolView = null
    const data = await this.withTokenRefresh(() => fetchProtocolForView(this.baseUrl, this.authToken, protocolId))
    this.protocolView = (data as ProtocolViewData) || null
    this.loadingProtocolView = false
  }

  @action loadProtocolForEdit = async (protocolId: number) => {
    this.loadingProtocolEdit = true
    this.protocolEdit = null
    const data = await this.withTokenRefresh(() => fetchProtocolForEdit(this.baseUrl, this.authToken, protocolId))
    this.protocolEdit = (data as ProtocolEditData) || null
    this.loadingProtocolEdit = false
  }

  @action updateProtocol = async (protocolId: number, body: UpdateProtocolBody) => {
    const res = await this.withTokenRefresh(() => updateProtocol(this.baseUrl, this.authToken, protocolId, body))
    if (res) {
      snackBar.showSuccess('הפרוטוקול עודכן בהצלחה')
    }
    return res
  }

  @action updateProtocolStatus = async (protocolId: number, body: UpdateProtocolStatusBody) => {
    return this.withTokenRefresh(() => updateProtocolStatus(this.baseUrl, this.authToken, protocolId, body))
  }

  @action loadProtocolCounts = async () => {
    const data = await this.withTokenRefresh(() => fetchProtocolCountsByStatus(this.baseUrl, this.authToken))
    if (data) this.protocolCounts = data as ProtocolCountsByStatus
  }

  @action loadArchive = async (pageNumber: number, pageSize: number) => {
    this.loadingArchive = true
    const data = await this.withTokenRefresh(() => fetchArchiveList(this.baseUrl, this.authToken, { pageNumber, pageSize }))
    console.log("DATA", data)
    this.archiveItems = (data as any)?.protocols || []
    this.archiveTotalCount = (data as any)?.totalCount || 0
    this.loadingArchive = false
  }

  @action loadUsers = async () => {
    const data = await this.withTokenRefresh(() => fetchUsers(this.baseUrl, this.authToken))
    this.users = (data as any) || []
  }

  @action loadUserPermissions = async () => {
    const data = await this.withTokenRefresh(() => fetchUserPermissions(this.baseUrl, this.authToken))
    this.userPermissions = (data as UserPermissions) || null
    return true
  }
}

const protocolsStore = new ProtocolsStore()
export default protocolsStore
